import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MoveHorizontal } from "lucide-react";
import { GALLERY } from "@/data/site";
import { Reveal } from "@/components/motion";

const CHAPTERS = ["The House", "The Kitchen", "The Table", "The People", "The Details"];

export default function Gallery() {
  const trackRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startLeft: 0, moved: false });
  const [filter, setFilter] = useState("All");

  const onDown = (e) => {
    const t = trackRef.current;
    drag.current = { down: true, startX: e.pageX, startLeft: t.scrollLeft, moved: false };
  };
  const onMove = (e) => {
    if (!drag.current.down) return;
    const t = trackRef.current;
    const dx = e.pageX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    t.scrollLeft = drag.current.startLeft - dx;
  };
  const onUp = () => (drag.current.down = false);
  const onWheel = (e) => {
    const t = trackRef.current;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      t.scrollLeft += e.deltaY;
    }
  };

  const items = filter === "All" ? GALLERY : GALLERY.filter((g) => g.chapter === filter);

  return (
    <div className="bg-brown text-cream min-h-screen" data-testid="page-gallery">
      <section className="pt-28 md:pt-32 pb-6 u-container" data-testid="gallery-header">
        <Reveal>
          <p className="u-eyebrow text-gold mb-3">The #VEEDU Gallery</p>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-tight max-w-4xl">
            A house. A kitchen. A table. A story.
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
            {["All", ...CHAPTERS].map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                className={`u-eyebrow link-line transition-colors ${
                  filter === c ? "text-gold" : "text-cream/50 hover:text-cream"
                }`}
              >
                {c}
              </button>
            ))}
            <span className="hidden md:inline-flex items-center gap-2 u-eyebrow text-cream/40 ml-auto">
              <MoveHorizontal size={16} /> Drag to explore
            </span>
          </div>
        </Reveal>
      </section>

      <div
        ref={trackRef}
        data-lenis-prevent
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onWheel={onWheel}
        className="no-scrollbar overflow-x-auto cursor-grab active:cursor-grabbing select-none pb-10"
        data-testid="gallery-track"
      >
        <div className="flex items-center gap-6 md:gap-10 px-6 md:px-20 min-h-[58vh]">
          {items.map((g, i) => (
            <figure
              key={i}
              className={`group relative shrink-0 ${
                g.size === "wide" ? "w-[85vw] md:w-[46vw]" : "w-[75vw] md:w-[30vw]"
              } ${i % 2 === 0 ? "self-start mt-4" : "self-end mb-4"}`}
              data-testid={`gallery-item-${i}`}
            >
              <div className={`overflow-hidden ${g.contain ? "bg-cream" : "bg-ink"} ${g.size === "wide" ? "aspect-[16/10]" : "aspect-[3/4]"}`}>
                <img
                  src={g.src}
                  alt={`${g.chapter} — ${g.caption}`}
                  draggable="false"
                  loading="lazy"
                  className={`w-full h-full transition-transform duration-[1300ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06] ${
                    g.contain ? "object-contain p-8" : "object-cover"
                  }`}
                />
                <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/30 transition-colors duration-700" />
              </div>
              <figcaption className="absolute left-0 bottom-0 p-5 opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                <span className="block w-8 h-px bg-gold mb-3 group-hover:w-14 transition-all duration-500" />
                <span className="u-eyebrow text-gold">{g.chapter}</span>
                <p className="font-display text-2xl text-cream mt-1">{g.caption}</p>
              </figcaption>
            </figure>
          ))}

          <div className="shrink-0 w-[70vw] md:w-[28vw] self-center pr-10">
            <p className="font-script text-gold text-3xl mb-4">to be continued</p>
            <p className="font-body font-light text-cream/60 mb-8 leading-relaxed">
              The story of #VEEDU is still being written — one service, one table, one tale at a time.
            </p>
            <Link
              to="/contact"
              data-testid="gallery-cta-contact"
              className="btn-veedu border-cream/40 text-cream hover:bg-terracotta hover:border-terracotta"
            >
              Come Home <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
